import { Button } from '../components/ui/button';
import { LeadForm } from '../components/forms/LeadForm';
import { FileCheck, Shield, Clock, CheckCircle, Calendar } from 'lucide-react';

const services = [
  {
    icon: FileCheck,
    title: 'Document Notarization',
    description: 'Professional notarization for all your real estate documents, contracts, and legal papers.'
  },
  {
    icon: Shield,
    title: 'Title Documents',
    description: 'Notarization of title transfers, deeds, and closing documents.'
  },
  {
    icon: Clock,
    title: 'Mobile Notary',
    description: 'Convenient mobile notary services - we come to you at your preferred location.'
  }
];

const documents = [
  'Real Estate Closing Documents',
  'Title Transfers & Deeds',
  'Power of Attorney',
  'Affidavits & Sworn Statements',
  'Mortgage Documents',
  'Trust Documents',
  'Contracts & Agreements',
  'Loan Documents'
];

export const NotaryServices = () => {
  return (
    <div data-testid="notary-page">
      {/* Hero Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="notary-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="section-overline">Notary Services</p>
            <h1 className="text-4xl sm:text-5xl font-light text-[#F5F5F0] mt-4 mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Professional <span className="text-gold-gradient">Notary Services</span>
            </h1>
            <p className="text-lg text-[#808080] max-w-2xl mx-auto">
              Convenient and professional notary services for all your real estate and legal document needs. Available for mobile appointments.
            </p>
            <div className="mt-8">
              <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
                <Button className="btn-gold" data-testid="notary-book-btn">
                  <Calendar className="w-4 h-4 mr-2" /> Schedule Appointment
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-[#111111]" data-testid="notary-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <p className="section-overline">What We Offer</p>
            <h2 className="section-title mt-2">Our Notary Services</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="card-hover bg-[#0a0a0a] p-8 rounded-lg text-center">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <service.icon className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-light text-[#F5F5F0] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                  {service.title}
                </h3>
                <p className="text-[#808080] text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="documents-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-overline">Documents We Notarize</p>
              <h2 className="section-title mt-2 mb-8">Common Documents</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {documents.map((doc, index) => (
                  <div key={index} className="flex items-center text-[#F5F5F0]">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mr-3 flex-shrink-0" />
                    <span className="text-sm">{doc}</span>
                  </div>
                ))}
              </div>
            </div>
            <LeadForm 
              source="notary"
              title="Request Notary Service"
              subtitle="Tell us about your notary needs and we'll get back to you promptly."
              buttonText="Request Service"
              showMessage={true}
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-[#111111]" data-testid="notary-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="section-title mb-8">Competitive Pricing</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="card-hover bg-[#0a0a0a] p-6 rounded-lg">
              <h3 className="text-[#D4AF37] text-3xl font-light mb-2" style={{ fontFamily: 'Playfair Display' }}>
                $15
              </h3>
              <p className="text-[#F5F5F0]">Per Notarized Signature</p>
              <p className="text-[#808080] text-sm mt-2">Standard FL rate</p>
            </div>
            <div className="card-hover bg-[#0a0a0a] p-6 rounded-lg border-2 border-[#D4AF37]">
              <h3 className="text-[#D4AF37] text-3xl font-light mb-2" style={{ fontFamily: 'Playfair Display' }}>
                $25+
              </h3>
              <p className="text-[#F5F5F0]">Mobile Service Fee</p>
              <p className="text-[#808080] text-sm mt-2">We come to you</p>
            </div>
            <div className="card-hover bg-[#0a0a0a] p-6 rounded-lg">
              <h3 className="text-[#D4AF37] text-3xl font-light mb-2" style={{ fontFamily: 'Playfair Display' }}>
                Call
              </h3>
              <p className="text-[#F5F5F0]">Closing Packages</p>
              <p className="text-[#808080] text-sm mt-2">Custom quotes available</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
